import React, { useState, useEffect, useCallback } from 'react';
import { Play, Calendar, User, Clock, ChevronRight, Youtube, Loader2, Search, Tag, X, Sparkles, Filter, ChevronDown, Heart } from 'lucide-react';
import { mockSermons } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// Favorites helpers
const getFavorites = () => {
  try { return JSON.parse(localStorage.getItem('sermon_favorites') || '[]'); } catch { return []; }
};
const toggleFavorite = (id) => {
  const favs = getFavorites();
  const next = favs.includes(id) ? favs.filter(f => f !== id) : [...favs, id];
  localStorage.setItem('sermon_favorites', JSON.stringify(next));
  return next;
};

const Sermons = () => {
  const { t, lang } = useLanguage();
  const [showVideo, setShowVideo] = useState(false);
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [allSpeakers, setAllSpeakers] = useState([]);
  const [activeTag, setActiveTag] = useState(null);
  const [activeSpeaker, setActiveSpeaker] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showFavsOnly, setShowFavsOnly] = useState(false);
  const [favorites, setFavorites] = useState(getFavorites());
  const [summary, setSummary] = useState('');
  const [summarizing, setSummarizing] = useState(false);
  const [summarySermonId, setSummarySermonId] = useState(null);

  const searchTexts = {
    de: { placeholder: 'Predigt suchen...', noResults: 'Keine Predigten gefunden.', clear: 'Filter zuruecksetzen', allTags: 'Alle', advancedFilter: 'Erweiterter Filter', speaker: 'Prediger', dateFrom: 'Von', dateTo: 'Bis', allSpeakers: 'Alle Prediger', favorites: 'Meine Favoriten', noFavorites: 'Noch keine Favoriten gespeichert.' },
    en: { placeholder: 'Search sermons...', noResults: 'No sermons found.', clear: 'Clear filter', allTags: 'All', advancedFilter: 'Advanced filter', speaker: 'Speaker', dateFrom: 'From', dateTo: 'To', allSpeakers: 'All speakers', favorites: 'My Favorites', noFavorites: 'No favorites saved yet.' },
    fr: { placeholder: 'Rechercher une predication...', noResults: 'Aucune predication trouvee.', clear: 'Reinitialiser le filtre', allTags: 'Tous', advancedFilter: 'Filtre avance', speaker: 'Predicateur', dateFrom: 'De', dateTo: 'A', allSpeakers: 'Tous les predicateurs', favorites: 'Mes favoris', noFavorites: 'Pas encore de favoris.' },
  };
  const st = searchTexts[lang] || searchTexts.de;

  // Fetch tags and speakers on mount
  useEffect(() => {
    fetch(`${API_BASE}/sermons/tags/all`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setAllTags(data); })
      .catch(err => console.error('Error fetching tags:', err));
    fetch(`${API_BASE}/sermons/speakers/all`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setAllSpeakers(data); })
      .catch(err => console.error('Error fetching speakers:', err));
  }, []);

  const loadSermons = useCallback(async (query = '', tag = null, speaker = '', from = '', to = '') => {
    setSearching(!!(query || tag || speaker || from || to));
    try {
      const isFiltered = !!(query || tag || speaker || from || to);
      let url = `${API_BASE}/sermons?limit=${isFiltered ? 50 : 4}`;
      if (query) url += `&search=${encodeURIComponent(query)}`;
      if (tag) url += `&tag=${encodeURIComponent(tag)}`;
      if (speaker) url += `&speaker=${encodeURIComponent(speaker)}`;
      if (from) url += `&date_from=${encodeURIComponent(from)}`;
      if (to) url += `&date_to=${encodeURIComponent(to)}`;

      const res = await fetch(url);
      const data = await res.json();
      if (data && data.length > 0) {
        setSermons(data);
      } else if (!isFiltered) {
        setSermons(mockSermons);
      } else {
        setSermons([]);
      }
    } catch (err) {
      console.error('Error loading sermons:', err);
      if (!(query || tag || speaker || from || to)) setSermons(mockSermons);
      else setSermons([]);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  }, []);

  useEffect(() => { loadSermons(); }, [loadSermons]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        loadSermons(searchQuery, activeTag, activeSpeaker, dateFrom, dateTo);
      } else if (searchQuery.length === 0) {
        loadSermons('', activeTag, activeSpeaker, dateFrom, dateTo);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, loadSermons, activeTag, activeSpeaker, dateFrom, dateTo]);

  const handleTagClick = (tag) => {
    const newTag = activeTag === tag ? null : tag;
    setActiveTag(newTag);
    loadSermons(searchQuery, newTag, activeSpeaker, dateFrom, dateTo);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setActiveTag(null);
    setActiveSpeaker('');
    setDateFrom('');
    setDateTo('');
    setShowAdvanced(false);
    loadSermons();
  };

  const activeFilterCount = [activeTag, activeSpeaker, dateFrom, dateTo].filter(Boolean).length;

  if (loading) {
    return (
      <section id="sermons" className="py-24 bg-white">
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#0f2440]" />
        </div>
      </section>
    );
  }

  const displaySermons = showFavsOnly ? sermons.filter(s => favorites.includes(s.id)) : sermons;
  const featured = displaySermons[0];
  const older = displaySermons.slice(1);

  const getEmbedUrl = (sermon) => {
    if (sermon.embed_url) return sermon.embed_url;
    if (sermon.video_id) return `https://www.youtube.com/embed/${sermon.video_id}?rel=0&modestbranding=1`;
    return null;
  };

  const getThumbnail = (sermon) => {
    if (sermon.thumbnail_url) return sermon.thumbnail_url;
    if (sermon.thumbnail) return sermon.thumbnail;
    if (sermon.video_id) return `https://img.youtube.com/vi/${sermon.video_id}/maxresdefault.jpg`;
    return 'https://images.unsplash.com/photo-1698768676413-e49b729dafa6?w=800';
  };

  const isFiltering = searchQuery || activeTag || activeSpeaker || dateFrom || dateTo;

  const summarizeSermon = async (sermon) => {
    if (summarizing) return;
    setSummarizing(true);
    setSummarySermonId(sermon.id);
    setSummary('');

    try {
      const response = await fetch(`${API_BASE}/ai/summarize-sermon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: sermon.title, description: sermon.description, speaker: sermon.speaker }),
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.text) setSummary(prev => prev + data.text);
            } catch (err) {
              console.error('Error parsing SSE chunk:', err);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error summarizing sermon:', err);
      setSummary('Fehler bei der Zusammenfassung.');
    } finally {
      setSummarizing(false);
    }
  };

  return (
    <section id="sermons" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-14">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">{t('sermons.title')}</h2>
          <div className="w-20 h-1 bg-[#0f2440] mx-auto mb-6"></div>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">{t('sermons.subtitle')}</p>

          {/* Search Bar */}
          <div className="max-w-md mx-auto mt-6 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={st.placeholder}
              data-testid="sermon-search"
              className="w-full pl-12 pr-4 py-3 bg-gray-100 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#0f2440]/30 focus:border-[#0f2440] focus:bg-white transition-all"
            />
            {searching && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />}
          </div>

          {/* Tag Filter */}
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center mt-5 max-w-2xl mx-auto" data-testid="sermon-tag-filters">
              <button
                onClick={() => { setActiveTag(null); loadSermons(searchQuery, null, activeSpeaker, dateFrom, dateTo); }}
                className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all border ${
                  !activeTag
                    ? 'bg-[#0f2440] text-white border-[#0f2440]'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-[#0f2440]/30'
                }`}
              >
                {st.allTags}
              </button>
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => handleTagClick(tag)}
                  data-testid={`sermon-filter-tag-${tag}`}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all border ${
                    activeTag === tag
                      ? 'bg-[#0f2440] text-white border-[#0f2440]'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#0f2440]/30'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}

          {/* Advanced Filter Toggle */}
          <div className="mt-4 flex justify-center gap-4">
            <button
              onClick={() => { setShowFavsOnly(!showFavsOnly); }}
              data-testid="sermon-favorites-toggle"
              className={`inline-flex items-center gap-2 text-sm transition-colors ${
                showFavsOnly ? 'text-red-600 font-semibold' : 'text-gray-500 hover:text-red-500'
              }`}
            >
              <Heart className={`w-4 h-4 ${showFavsOnly ? 'fill-current' : ''}`} />
              {st.favorites}
              {favorites.length > 0 && (
                <span className="bg-red-100 text-red-600 text-[10px] px-1.5 py-0.5 rounded-full">{favorites.length}</span>
              )}
            </button>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              data-testid="sermon-advanced-filter-toggle"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#0f2440] transition-colors"
            >
              <Filter className="w-4 h-4" />
              {st.advancedFilter}
              {activeFilterCount > 0 && (
                <span className="bg-[#0f2440] text-white text-[10px] px-1.5 py-0.5 rounded-full">{activeFilterCount}</span>
              )}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Advanced Filter Panel */}
          {showAdvanced && (
            <div className="mt-4 max-w-xl mx-auto bg-gray-50 rounded-xl p-5 border border-gray-200 space-y-4" data-testid="sermon-advanced-filters">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Speaker */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">{st.speaker}</label>
                  <select
                    value={activeSpeaker}
                    onChange={(e) => setActiveSpeaker(e.target.value)}
                    data-testid="sermon-filter-speaker"
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0f2440]/30 focus:border-[#0f2440]"
                  >
                    <option value="">{st.allSpeakers}</option>
                    {allSpeakers.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                {/* Date From */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">{st.dateFrom}</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    data-testid="sermon-filter-date-from"
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0f2440]/30 focus:border-[#0f2440]"
                  />
                </div>
                {/* Date To */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">{st.dateTo}</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    data-testid="sermon-filter-date-to"
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0f2440]/30 focus:border-[#0f2440]"
                  />
                </div>
              </div>
              {isFiltering && (
                <div className="flex justify-end">
                  <button
                    onClick={clearFilters}
                    data-testid="sermon-clear-filters"
                    className="text-xs text-red-500 hover:text-red-700 font-medium flex items-center gap-1"
                  >
                    <X className="w-3.5 h-3.5" /> {st.clear}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="max-w-6xl mx-auto">
          {/* No results */}
          {sermons.length === 0 && isFiltering && (
            <div className="text-center py-12" data-testid="sermon-no-results">
              <p className="text-gray-500 text-lg mb-3">{st.noResults}</p>
              <button onClick={clearFilters} className="text-[#0f2440] font-medium hover:underline">{st.clear}</button>
            </div>
          )}

          {/* Featured Sermon */}
          {featured && (
            <div className="mb-12" data-testid="featured-sermon">
              <h3 className="text-sm font-semibold text-[#0f2440] uppercase tracking-wider mb-4">
                {t('sermons.latest')}
              </h3>
              <div className="grid lg:grid-cols-5 gap-0 bg-gray-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <div className="lg:col-span-3 relative aspect-video lg:aspect-auto cursor-pointer group"
                     onClick={() => setShowVideo(!showVideo)}>
                  {showVideo && getEmbedUrl(featured) ? (
                    <iframe
                      src={getEmbedUrl(featured)}
                      title={featured.title}
                      className="w-full h-full min-h-[300px]"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <>
                      <img src={getThumbnail(featured)} alt={featured.title}
                        className="w-full h-full min-h-[300px] object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center group-hover:bg-white group-hover:scale-110 transition-all shadow-xl">
                          <Play className="w-9 h-9 text-[#0f2440] ml-1" />
                        </div>
                      </div>
                      {featured.duration && (
                        <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                          {featured.duration}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="lg:col-span-2 p-6 lg:p-8 flex flex-col justify-center">
                  <h4 className="text-2xl font-bold text-gray-900 mb-3 leading-snug">{featured.title}</h4>
                  <button
                    onClick={(e) => { e.stopPropagation(); setFavorites(toggleFavorite(featured.id)); }}
                    data-testid="featured-fav-btn"
                    className={`absolute top-4 right-4 p-2 rounded-full transition-all shadow-md ${
                      favorites.includes(featured.id) ? 'bg-red-500 text-white' : 'bg-white/80 text-gray-400 hover:text-red-500'
                    }`}
                  >
                    <Heart className={`w-5 h-5 ${favorites.includes(featured.id) ? 'fill-current' : ''}`} />
                  </button>
                  <p className="text-gray-600 mb-4 leading-relaxed">{featured.description}</p>
                  {/* Tags */}
                  {featured.tags && featured.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {featured.tags.map(tag => (
                        <span key={tag} className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="space-y-2 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span>{featured.speaker}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span>{new Date(featured.date).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    </div>
                    {featured.duration && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span>{featured.duration}</span>
                      </div>
                    )}
                  </div>
                  <button onClick={() => setShowVideo(true)} data-testid="featured-sermon-play"
                    className="mt-6 px-6 py-3 bg-[#0f2440] text-white rounded-lg font-semibold hover:bg-[#1a3a5c] transition-all hover:scale-105 inline-flex items-center gap-2 w-fit">
                    <Play className="w-4 h-4" />{t('sermons.watchNow')}
                  </button>
                  <button onClick={() => summarizeSermon(featured)} data-testid="featured-sermon-summarize"
                    disabled={summarizing}
                    className="mt-3 px-5 py-2.5 bg-amber-500/10 border border-amber-500/30 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-500/20 transition-all inline-flex items-center gap-2 w-fit disabled:opacity-50">
                    {summarizing && summarySermonId === featured.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    KI-Zusammenfassung
                  </button>
                  {summary && summarySermonId === featured.id && (
                    <div className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="sermon-summary">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-amber-600" />
                        <span className="text-amber-800 text-xs font-semibold">KI-Zusammenfassung</span>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed">{summary}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Older Sermons */}
          {older.length > 0 && (
            <div data-testid="older-sermons">
              <h3 className="text-sm font-semibold text-[#0f2440] uppercase tracking-wider mb-4">
                {t('sermons.more')}
              </h3>
              <div className="grid sm:grid-cols-3 gap-6">
                {older.map((sermon) => (
                  <div key={sermon.id} data-testid={`sermon-card-${sermon.id}`}
                    className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 hover:-translate-y-1">
                    <div className="relative aspect-video overflow-hidden">
                      <img src={getThumbnail(sermon)} alt={sermon.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-all">
                          <Play className="w-5 h-5 text-[#0f2440] ml-0.5" />
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setFavorites(toggleFavorite(sermon.id)); }}
                        data-testid={`sermon-fav-${sermon.id}`}
                        className={`absolute top-2 right-2 p-1.5 rounded-full transition-all z-10 ${
                          favorites.includes(sermon.id) ? 'bg-red-500 text-white' : 'bg-white/80 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100'
                        }`}
                      >
                        <Heart className={`w-4 h-4 ${favorites.includes(sermon.id) ? 'fill-current' : ''}`} />
                      </button>
                      {sermon.duration && (
                        <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium">
                          {sermon.duration}
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h4 className="font-bold text-gray-900 mb-1 group-hover:text-[#0f2440] transition-colors line-clamp-1">{sermon.title}</h4>
                      <p className="text-gray-500 text-sm line-clamp-2 mb-2">{sermon.description}</p>
                      {/* Tags */}
                      {sermon.tags && sermon.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {sermon.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full text-[10px] font-medium">
                              {tag}
                            </span>
                          ))}
                          {sermon.tags.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-[10px] font-medium">
                              +{sermon.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
                        <span>{sermon.speaker}</span>
                        <span>{new Date(sermon.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* YouTube CTA */}
          <div className="text-center mt-10">
            <a href="https://youtube.com/@icg.hannover" target="_blank" rel="noopener noreferrer" data-testid="sermons-youtube-btn"
              className="px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-500 transition-all hover:scale-105 inline-flex items-center gap-2">
              <Youtube className="w-5 h-5" />{t('sermons.allOnYoutube')}<ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Sermons;
